import { useEffect, useState } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import {
  usePreApproveBayHireRequest,
  useUploadBayHireAttachmentMetadata,
} from "@features/bay-hire/hooks/useBayHireMutations";
import { useDownloadBayHireAttachment } from "@features/bay-hire/hooks/useDownloadBayHireAttachment";
import {
  makePreApproveDialogSchema,
  preApproveDialogDefaultValues,
  type PreApproveDialogFormData,
} from "@features/bay-hire/schema/preApproveDialogSchema";
import { validateBayHireAttachmentFile } from "@features/bay-hire/utils/bayHireFileValidation";
import { getApiErrorDetail } from "@features/bay-hire/utils/getApiErrorDetail";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  bayHireService,
  type PreApproveRequest,
  type StagedAttachment,
} from "@services/bayHireApi";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface UsePreApproveDialogProps {
  open: boolean;
  requestId: string;
  /** Vendor-requested window, used to seed the approved dates (see the open effect). */
  dateFrom?: string | null;
  dateTo?: string | null;
  /** Already-approved window, if this request has been through pre-approval before. */
  dateFromApproved?: string | null;
  dateToApproved?: string | null;
  /**
   * DFT-0044 bug fix (2026-08-03): needed so the form can refuse a non-positive Total Amount on a
   * chargeable request. Without it, a blank amount was coerced to 0, the domain answered 400 and the
   * Temporal workflow failed PERMANENTLY, stranding the request at REQUESTED. See the retraction
   * note in preApproveDialogSchema.ts.
   */
  paymentType?: string | null;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

interface UsePreApproveDialogResult {
  form: ReturnType<typeof useForm<PreApproveDialogFormData>>;
  /** DFT-0103: presence signals a staged-or-registered HOD document (gates the SingleFile view). */
  hodStagedAttachment: StagedAttachment | null;
  hodFileName: string | null;
  isViewingHod: boolean;
  submitError: string | null;
  isSubmitting: boolean;
  /** DFT-0044 (FE-60): true while the "Confirm Bay Hire Pre-Approval" step is open. */
  showConfirm: boolean;
  /** DFT-0103: true while the HOD slot's file is being staged (SAS mint + blob PUT). */
  isHodStaging: boolean;
  hodFiles: UploadEntry[];
  setHodFiles: (files: UploadEntry[]) => void;
  handleHodUpload: (selected: File[]) => Promise<void>;
  handleViewHodDocument: () => Promise<void>;
  handleRemoveHodDocument: () => void;
  /**
   * DFT-0044 (FE-73): a second, generic Attachments upload, separate from the HOD document -
   * `05_Port_License_details-19.png` shows both. Posted as `SUPPORTING_DOCUMENT` (already an
   * allowed type on the per-request upload endpoint, mirroring how Reject/Cancel's own optional
   * upload works, F3) - needs no new backend field, since it is recorded against the request
   * directly rather than carried as a field on the `PreApproveRequest` body itself.
   */
  supportingDocumentStagedAttachment: StagedAttachment | null;
  supportingDocumentFileName: string | null;
  supportingDocumentFiles: UploadEntry[];
  setSupportingDocumentFiles: (files: UploadEntry[]) => void;
  handleSupportingDocumentUpload: (selected: File[]) => Promise<void>;
  handleRemoveSupportingDocument: () => void;
  /** DFT-0103: true while the supporting-document slot's file is being staged. */
  isSupportingDocumentStaging: boolean;
  /** Validates the form and opens the confirm step - does NOT submit yet. */
  handleSubmitForm: (data: PreApproveDialogFormData) => void;
  /** Actually submits the pre-approval, called from the confirm dialog's own Confirm button. */
  handleConfirmSubmit: () => Promise<void>;
  /** Closes the confirm step without submitting, leaving the form as the user left it. */
  handleCancelConfirm: () => void;
  /** DFT-0106: wraps the raw `onClose` prop with the staged-state reset below - see that note. */
  handleClose: () => void;
}

export const usePreApproveDialog = (props: UsePreApproveDialogProps): UsePreApproveDialogResult => {
  const {
    open,
    requestId,
    dateFrom,
    dateTo,
    dateFromApproved,
    dateToApproved,
    paymentType,
    onClose,
    onSuccess,
  } = props;
  // Mirrors the domain's own derivation (BayHireRequestService: !EXEMPTED => chargeable). Unknown
  // payment type is treated as chargeable, i.e. the safer side - it asks for an amount rather than
  // letting a blank one through to a terminal 400.
  const isChargeable = paymentType !== "EXEMPTED";
  const { t } = useTranslation();
  // DFT-0044 (FE-13/FE-70): same profile the create form's own Attachments upload already
  // enforces - see bayHireFileValidation.ts. DFT-0088 addendum (2026-09-01): widened from
  // pdf/jpg/png-only to pdf/jpg/jpeg/png/bmp/gif plus Excel/Word, 10 MB - see
  // FileUpload.constants.ts's `bayHireAttachment` profile.
  const uploadConditions = useFileUploadProfiles().bayHireAttachment;
  // DFT-0103: staged on drop (blob-only, no backend attach call); durably registered only at
  // confirm-submit time (handleConfirmSubmit), mirroring `BayHireCreateAttachmentUpload.tsx`'s
  // own stage-then-register pattern.
  const [hodStagedAttachment, setHodStagedAttachment] = useState<StagedAttachment | null>(null);
  const [hodFiles, setHodFiles] = useState<UploadEntry[]>([]);
  const [isHodStaging, setIsHodStaging] = useState(false);
  // FE-73: independent of the HOD state above - a separate optional upload, never a gate.
  const [supportingDocumentStagedAttachment, setSupportingDocumentStagedAttachment] =
    useState<StagedAttachment | null>(null);
  const [supportingDocumentFiles, setSupportingDocumentFiles] = useState<UploadEntry[]>([]);
  const [isSupportingDocumentStaging, setIsSupportingDocumentStaging] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // DFT-0044 (FE-60): mirrors Gatepass's ApproveGatepassDrawer two-step confirm pattern -
  // showConfirm + pendingRequest hold the validated payload between "Pre-approve" (validates,
  // opens the confirm step) and the confirm dialog's own Confirm button (actually submits).
  const [showConfirm, setShowConfirm] = useState(false);
  // DFT-0103: `hodAttachmentId` is intentionally omitted here - it is not known until
  // `handleConfirmSubmit` resolves the (possibly still-unregistered) staged HOD attachment.
  const [pendingRequest, setPendingRequest] = useState<Omit<
    PreApproveRequest,
    "hodAttachmentId"
  > | null>(null);

  const hodFileName = hodStagedAttachment?.originalFileName ?? null;
  const supportingDocumentFileName = supportingDocumentStagedAttachment?.originalFileName ?? null;

  const { isBusy, openAttachment } = useDownloadBayHireAttachment({
    onError: setSubmitError,
    errorMessage: t("bay-hire.pre-approve.hod-view-error"),
  });
  const isViewingHod = hodStagedAttachment?.documentUrl
    ? isBusy(hodStagedAttachment.documentUrl)
    : false;

  const preApproveMutation = usePreApproveBayHireRequest();
  const registerAttachmentMutation = useUploadBayHireAttachmentMetadata();

  const form = useForm<PreApproveDialogFormData>({
    resolver: zodResolver(makePreApproveDialogSchema(isChargeable, t)),
    defaultValues: preApproveDialogDefaultValues,
  });

  // Operations adjusts the vendor's requested window rather than retyping it, so
  // the dialog opens on the request's dates. Approved dates win when present (a
  // request that has been pre-approved before), otherwise the requested ones.
  // Seeding on open rather than via defaultValues is deliberate: this component
  // stays mounted across requests, so defaultValues would only ever apply once.
  // Operations adjusts the vendor's requested window rather than retyping it, so the dialog
  // opens on the request's dates. Approved dates win when present, otherwise the requested ones.
  useEffect(() => {
    if (!open) return;
    form.reset({
      ...preApproveDialogDefaultValues,
      dateFromApproved: dateFromApproved ?? dateFrom ?? "",
      dateToApproved: dateToApproved ?? dateTo ?? "",
    });
  }, [open, form, dateFrom, dateTo, dateFromApproved, dateToApproved]);

  // DFT-0106: this used to be the `!open` branch of the effect above - but this dialog's real
  // parent (`BayHireRequestDetail.tsx`) mounts/unmounts it entirely per row
  // (`{openDrawers.preApprove && <PreApproveDialog open .../>}`, `open` a literal `true` at every
  // real call site), so `open` never actually transitions to `false` on a persistently-mounted
  // instance - that branch was dead code in production: staging an HOD/supporting document for
  // one request, then closing without submitting, left both this hook's own local state (the
  // supporting-document half was never reset here even in the old effect) AND the shared
  // useUploadStore entries stuck, silently leaking into whichever request's dialog opened next.
  // Reset is now explicit, called from `handleClose` (wraps the raw `onClose` prop) and after a
  // successful submit, mirroring `ReasonDialog.tsx`'s own already-correct pattern.
  const resetStagedState = () => {
    setHodStagedAttachment(null);
    setHodFiles([]);
    setSupportingDocumentStagedAttachment(null);
    setSupportingDocumentFiles([]);
    setSubmitError(null);
    setShowConfirm(false);
    setPendingRequest(null);
    form.reset(preApproveDialogDefaultValues);
    useUploadStore.getState().clearFilesForKey("HOD_APPROVAL");
    useUploadStore.getState().clearFilesForKey("SUPPORTING_DOCUMENT");
  };

  const handleClose = () => {
    resetStagedState();
    onClose();
  };

  // DFT-0050 (AC30/AC31): rebuilt on the shared `FileUpload` component (`frontend-guidelines.md`
  // §3b/3c named this dialog's previous raw `<input>` ref as a known gap) - `onUpload` hands back
  // the dropzone's accepted files, matching `ReasonDialog.handleUpload`'s shape.
  //
  // DFT-0103: stages the blob only (SAS direct-to-Azure, no backend attach call) - the durable
  // per-request registration is deferred to `handleConfirmSubmit`, matching
  // `BayHireCreateAttachmentUpload.tsx`'s own `handleUpload`.
  const handleHodUpload = async (selected: File[]) => {
    const file = selected[selected.length - 1];
    if (!file) return;

    const validation = validateBayHireAttachmentFile(file, uploadConditions);
    if (!validation.ok) {
      setHodFiles([]);
      setSubmitError(
        validation.reason === "size"
          ? t("bay-hire.pre-approve.file-too-large")
          : t("bay-hire.pre-approve.unsupported-file-type", {
              formats: uploadConditions.format?.join(", "),
              defaultValue: `Unsupported file type. Allowed: ${uploadConditions.format?.join(", ")}.`,
            }),
      );
      return;
    }

    setSubmitError(null);
    setIsHodStaging(true);
    try {
      const staged = await bayHireService.uploadStagedAttachment(file, "HOD_APPROVAL");
      setHodStagedAttachment(staged);
      // DFT-0096: overwrite FileUpload's own auto-synced entry (the raw,
      // not-meaningfully-serializable File object) with the real uploaded
      // reference, mirroring SingleCertificate.tsx's onSuccess - name/url,
      // not path/relativePath, is what should survive a refresh.
      useUploadStore.getState().setFilesForKey("HOD_APPROVAL", [
        {
          name: staged.originalFileName ?? file.name,
          url: staged.documentUrl ?? "",
          attachmentType: "HOD_APPROVAL",
        },
      ]);
    } catch (error) {
      console.error("Failed to upload HOD approval document:", error);
      setHodFiles([]);
      useUploadStore.getState().clearFilesForKey("HOD_APPROVAL");
      setSubmitError(t("bay-hire.pre-approve.hod-upload-error"));
    } finally {
      setIsHodStaging(false);
    }
  };

  // DFT-0036: the uploaded HOD document is viewed through the mediated
  // download (Bay Hire never exposes raw Document Service URLs to the browser).
  // DFT-0027: that mediated access now lives in useDownloadBayHireAttachment,
  // shared with the Request Detail page's downloadable attachments.
  //
  // DFT-0103 (AD4): gates on `documentUrl` alone, not on a registered attachment id -
  // `useDownloadBayHireAttachment`'s own `attachmentId` parameter is unused (kept only for
  // call-site compatibility), so viewing already works identically whether the HOD document is
  // staged-only (pre-submit) or durably registered (post-submit).
  const handleViewHodDocument = async () => {
    if (!hodStagedAttachment?.documentUrl) return;
    setSubmitError(null);
    await openAttachment(requestId, hodStagedAttachment.id ?? "", hodStagedAttachment.documentUrl);
  };

  const handleRemoveHodDocument = () => {
    setHodStagedAttachment(null);
    setHodFiles([]);
    setSubmitError(null);
    useUploadStore.getState().clearFilesForKey("HOD_APPROVAL");
  };

  // FE-73: mirrors handleHodUpload, but for the second, generic upload - posted as
  // SUPPORTING_DOCUMENT, never required, and unrelated to the HOD validation path above.
  //
  // DFT-0103: stages the blob only, same as handleHodUpload above.
  const handleSupportingDocumentUpload = async (selected: File[]) => {
    const file = selected[selected.length - 1];
    if (!file) return;

    const validation = validateBayHireAttachmentFile(file, uploadConditions);
    if (!validation.ok) {
      setSupportingDocumentFiles([]);
      setSubmitError(
        validation.reason === "size"
          ? t("bay-hire.pre-approve.file-too-large")
          : t("bay-hire.pre-approve.unsupported-file-type", {
              formats: uploadConditions.format?.join(", "),
              defaultValue: `Unsupported file type. Allowed: ${uploadConditions.format?.join(", ")}.`,
            }),
      );
      return;
    }

    setSubmitError(null);
    setIsSupportingDocumentStaging(true);
    try {
      const staged = await bayHireService.uploadStagedAttachment(file, "SUPPORTING_DOCUMENT");
      setSupportingDocumentStagedAttachment(staged);
      // DFT-0096: see handleHodUpload's identical comment above.
      useUploadStore.getState().setFilesForKey("SUPPORTING_DOCUMENT", [
        {
          name: staged.originalFileName ?? file.name,
          url: staged.documentUrl ?? "",
          attachmentType: "SUPPORTING_DOCUMENT",
        },
      ]);
    } catch (error) {
      console.error("Failed to upload supporting document:", error);
      setSupportingDocumentFiles([]);
      useUploadStore.getState().clearFilesForKey("SUPPORTING_DOCUMENT");
      setSubmitError(t("bay-hire.pre-approve.supporting-document-upload-error"));
    } finally {
      setIsSupportingDocumentStaging(false);
    }
  };

  const handleRemoveSupportingDocument = () => {
    setSupportingDocumentStagedAttachment(null);
    setSupportingDocumentFiles([]);
    setSubmitError(null);
    useUploadStore.getState().clearFilesForKey("SUPPORTING_DOCUMENT");
  };

  // DFT-0044 (FE-60): `05_Port_License_details-20.png` (PoF Visuals_Bay Hire 2) shows a "Confirm
  // Bay Hire Pre-Approval" step between the drawer's own Pre-approve button and the request
  // actually being pre-approved. This now only VALIDATES (react-hook-form's handleSubmit already
  // guarantees `data` is valid by the time this runs) and stashes the request, opening the confirm
  // step - it no longer submits directly.
  const handleSubmitForm = (data: PreApproveDialogFormData) => {
    // 🔴 DFT-0044 bug fix (2026-08-03): the same class of defect as the Total Amount fix above,
    // on a different input. Observed live: Ops pre-approved a YEARLY request with no HOD document
    // attached, three times in a row, each with the SAME generic "Status has not changed yet"
    // toast and no indication of why - `hodAttachmentId: null` every time in the Temporal signal
    // payload. The domain correctly rejects YEARLY-without-HOD (D25/D39), BayHireDomainProxy
    // classifies the 4xx as a NON-RETRYABLE ApplicationFailure, and even with the workflow-level
    // recovery fix (bay-hire-workflow-worker `cdb73f6` - the workflow itself no longer dies, a
    // fresh task is created each time) the SYNCHRONOUS caller still cannot learn WHY it keeps
    // failing, because `:pre-approve` is inherently async (a Signal, not an Update). Blocking here
    // is the only point in the whole chain where the actual reason can still reach the user.
    if (paymentType === "YEARLY" && !hodStagedAttachment) {
      setSubmitError(t("bay-hire.pre-approve.hod-required-for-yearly"));
      return;
    }

    const amount = Number(data.totalAmount);
    // DFT-0103: `hodAttachmentId` is resolved later, in `handleConfirmSubmit` - a freshly staged
    // HOD document has no id yet, so it cannot be included here.
    setPendingRequest({
      dateFromApproved: data.dateFromApproved,
      dateToApproved: data.dateToApproved,
      // DFT-0044: chargeable and bayAssignments are gone from the contract. chargeable is derived
      // from paymentType server-side (D42) and Operations no longer selects bays (D35/D43).
      totalAmount: amount || 0,
      preApprovalRemarks: data.preApprovalRemarks || undefined,
    });
    setSubmitError(null);
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingRequest) return;
    setSubmitError(null);
    try {
      // DFT-0103 (AD3/AD5): each staged-and-unregistered slot is registered independently,
      // immediately before the real `preApprove` call, mirroring
      // `useBayHireContainer.tsx`'s `handleUpdateFormSubmit`/`handleResubmitFormSubmit`. Both
      // awaits sit in this same try/catch (AC10: a registration failure for either slot fails
      // closed - `preApprove` is never called, and both slots remain staged for a retry).
      let hodAttachmentId: string | undefined = hodStagedAttachment?.id;
      if (hodStagedAttachment && !hodStagedAttachment.id) {
        const registered = await registerAttachmentMutation.mutateAsync({
          id: requestId,
          request: {
            attachmentType: hodStagedAttachment.attachmentType,
            documentUrl: hodStagedAttachment.documentUrl,
            originalFileName: hodStagedAttachment.originalFileName,
            contentType: hodStagedAttachment.contentType,
            fileSizeBytes: hodStagedAttachment.fileSizeBytes,
          },
        });
        hodAttachmentId = registered.id;
      }

      // AD5: the supporting-document slot is registered too (so no orphaned row is left before
      // confirm, symmetrically with the HOD slot), but per the pre-existing, out-of-scope
      // `PreApproveRequest` contract gap (task.md Non-Goals), its resulting id is never sent.
      if (supportingDocumentStagedAttachment && !supportingDocumentStagedAttachment.id) {
        await registerAttachmentMutation.mutateAsync({
          id: requestId,
          request: {
            attachmentType: supportingDocumentStagedAttachment.attachmentType,
            documentUrl: supportingDocumentStagedAttachment.documentUrl,
            originalFileName: supportingDocumentStagedAttachment.originalFileName,
            contentType: supportingDocumentStagedAttachment.contentType,
            fileSizeBytes: supportingDocumentStagedAttachment.fileSizeBytes,
          },
        });
      }

      await preApproveMutation.mutateAsync({
        id: requestId,
        request: { ...pendingRequest, hodAttachmentId },
      });
      // DFT-0106: same reasoning as handleClose - the dialog is unmounted right after this by
      // its parent, not toggled `open={false}`, so this is the only reliable place left to clear
      // staged state before it could otherwise leak into the next request this dialog opens for.
      resetStagedState();
      await onSuccess();
    } catch (error) {
      console.error("Pre-approval submission failed:", error);
      setShowConfirm(false);
      // DFT-0077 (AD-5/AC16): pre-approve is one of the signal-based Ops decisions - a domain-side
      // rejection may never reach here as an HTTP error at all, so the fallback below is what most
      // users actually see for this flow. Not a gap: getApiErrorDetail still surfaces the
      // synchronous case (e.g. no active claimed task) when one IS returned.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.pre-approve.submit-error")));
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingRequest(null);
  };

  return {
    form,
    hodStagedAttachment,
    hodFileName,
    isViewingHod,
    submitError,
    isSubmitting: registerAttachmentMutation.isPending || preApproveMutation.isPending,
    showConfirm,
    isHodStaging,
    hodFiles,
    setHodFiles,
    handleHodUpload,
    handleViewHodDocument,
    handleRemoveHodDocument,
    supportingDocumentStagedAttachment,
    supportingDocumentFileName,
    supportingDocumentFiles,
    setSupportingDocumentFiles,
    handleSupportingDocumentUpload,
    handleRemoveSupportingDocument,
    handleClose,
    isSupportingDocumentStaging,
    handleSubmitForm,
    handleConfirmSubmit,
    handleCancelConfirm,
  };
};
